import { useEffect, useMemo, useState } from "react";

import { FormProvider, UseFormReturn } from "react-hook-form";

import Box from "@mui/material/Box";
import FormLabel from "@mui/material/FormLabel";
import Stack from "@mui/material/Stack";

import {
  EffectiveDateExposureAndLapseForm,
  PriorInsuranceStatusRadioGroup,
  ResidencyTypeRadioGroup,
  TimeSpentAtHomeRadioGroup,
} from "@covertree/new-business-forms";
import { CoverageEffectiveDate, Form, SubmitButton } from "@covertree/ui";
import { ResidencyType } from "@covertree/types";

import { AWS_DATE_LENGTH, NEW_POLICY_SUBMIT_BUTTON_ID } from "@consts/index";
import { useScrollToNextButton } from "@hooks/scroll/useScrollToNextButton";
import {
  useNewBusinessSelector,
  selectIsBookConversion,
  selectPolicyUsage,
} from "@covertree/new-business-redux";
import { PolicyUsage } from "@covertree/new-business-graphql";

interface EffectiveDateExposureAndLapseViewProps {
  form: UseFormReturn<EffectiveDateExposureAndLapseForm>;
  onSubmit: (formValues: EffectiveDateExposureAndLapseForm) => Promise<void>;
  loading: boolean;
}

export function EffectiveDateExposureAndLapseView({
  form,
  onSubmit,
  loading,
}: EffectiveDateExposureAndLapseViewProps) {
  const isBookConversion = useNewBusinessSelector(selectIsBookConversion);
  const policyUsage = useNewBusinessSelector(selectPolicyUsage);
  const {
    control,
    handleSubmit,
    formState: { isValid, errors, isSubmitSuccessful },
    watch,
    getValues,
  } = form;
  // Initialize state from form value so dependent field inputs render before shouldUnregister drops the fields
  const [wasEffectiveDateFilled, setWasEffectiveDateFilled] = useState(
    () => getValues("policyEffectiveDate")?.length === AWS_DATE_LENGTH
  );

  const policyEffectiveDate = watch("policyEffectiveDate");
  const residencyType = watch("residencyType");

  const isRRFlow = useMemo(
    () => residencyType === ResidencyType.Tenant,
    [residencyType]
  );
  const isRental = useMemo(
    () => policyUsage === PolicyUsage.Rental,
    [policyUsage]
  );

  useEffect(() => {
    if (
      !wasEffectiveDateFilled &&
      policyEffectiveDate?.length === AWS_DATE_LENGTH &&
      !errors?.policyEffectiveDate
    ) {
      setWasEffectiveDateFilled(true);
    }
  }, [policyEffectiveDate, wasEffectiveDateFilled]);

  useScrollToNextButton(isValid && !isSubmitSuccessful);

  return (
    <Box mt={3}>
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="column">
            <FormLabel
              sx={{
                display: "flex",
                mb: -2,
              }}>
              Coverage Effective Date
            </FormLabel>
            <CoverageEffectiveDate
              control={control}
              name="policyEffectiveDate"
              displayInCard={false}
              disabled={isBookConversion}
              withHeader={false}
            />
          </Stack>
          {wasEffectiveDateFilled && (
            <Stack direction="column" mt={4} spacing={3}>
              {!isRental && <ResidencyTypeRadioGroup isD2C={false} />}

              {!isRental && residencyType && !isRRFlow ? (
                <TimeSpentAtHomeRadioGroup isD2C={false} />
              ) : null}
              {residencyType || isRental ? (
                <PriorInsuranceStatusRadioGroup
                  isRRFlow={isRRFlow}
                  isD2C={false}
                />
              ) : null}
            </Stack>
          )}
          <SubmitButton
            disabled={!isValid || loading}
            loading={loading}
            width={100}
            id={NEW_POLICY_SUBMIT_BUTTON_ID}>
            Next
          </SubmitButton>
        </Form>
      </FormProvider>
    </Box>
  );
}
